import { useEffect, useState } from "react";
import type { CSSProperties } from "react";
import { AlertTriangle, ChevronDown, CircleDollarSign, Flag, Target, Wallet } from "lucide-react";
import { getBudgets } from "../api/client.js";
import type { BudgetItem, BudgetsResponse, SectionError } from "../api/types.js";
import { useApi } from "../lib/useApi.js";
import { budgetGeometry } from "../lib/chart-math.js";
import type { BudgetGeometry, BudgetTone } from "../lib/chart-math.js";
import { BudgetBar } from "../components/charts.js";
import { Card, EmptyState, KpiCard, Pill, StatusDot } from "../components/ui.js";

export interface BudgetsPageProps {
  refreshKey: number;
  onGeneratedAt?: (iso: string) => void;
}

interface Scope {
  label: string;
  color: string;
}

function budgetScope(name: string): Scope {
  const n = name.toLowerCase();
  if (n.includes("management") || n.includes("platform")) {
    return { label: "all accounts · consolidated", color: "var(--acct-shared)" };
  }
  if (n.includes("staging")) return { label: "jammsante-staging", color: "var(--acct-staging)" };
  if (n.includes("prod")) return { label: "jammsante-prod", color: "var(--acct-prod)" };
  if (n.includes("shared")) return { label: "azertica-shared", color: "var(--acct-shared)" };
  if (n.includes("dev") || n.includes("sandbox")) return { label: "sandbox", color: "var(--acct-dev)" };
  return { label: "monthly", color: "var(--tone-nodata-dot)" };
}

function money(value: number, currency: string, digits = 0): string {
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }).format(value);
  } catch {
    return `${value.toFixed(digits)} ${currency}`;
  }
}

type StatusToneKey = "alarm" | "warn" | "ok";

interface BudgetView {
  budget: BudgetItem;
  scope: Scope;
  geo: BudgetGeometry;
  actualPct: number | null;
  forecastPct: number | null;
  worstPct: number;
  pillTone: StatusToneKey;
  statusLabel: string;
  flagged: boolean;
}

function toView(budget: BudgetItem): BudgetView {
  const actualPct = budget.limit > 0 ? (budget.actualSpend / budget.limit) * 100 : null;
  const forecastPct =
    budget.limit > 0 && budget.forecastedSpend !== null
      ? (budget.forecastedSpend / budget.limit) * 100
      : null;
  const worstPct = Math.max(actualPct ?? 0, forecastPct ?? 0);
  const pillTone: StatusToneKey = worstPct > 100 ? "alarm" : worstPct > 90 ? "warn" : "ok";
  const statusLabel =
    actualPct !== null && actualPct > 100
      ? "Over limit"
      : forecastPct !== null && forecastPct > 100
        ? "Forecast exceeds"
        : worstPct > 90
          ? "Approaching"
          : "On track";
  return {
    budget,
    scope: budgetScope(budget.name),
    geo: budgetGeometry(budget.actualSpend, budget.forecastedSpend, budget.limit),
    actualPct,
    forecastPct,
    worstPct,
    pillTone,
    statusLabel,
    flagged: budget.status !== "under",
  };
}

const TONE_FG: Record<StatusToneKey, string> = {
  alarm: "var(--tone-alarm-fg)",
  warn: "var(--tone-warn-fg)",
  ok: "var(--tone-ok-fg)",
};

const FILL_COLOR: Record<BudgetTone, string> = {
  alarm: "var(--tone-alarm-dot)",
  warn: "var(--tone-warn-dot)",
  accent: "var(--accent)",
};

function ScopePill({ scope }: { scope: Scope }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "2px 9px",
        borderRadius: "var(--radius-full)",
        background: "var(--bg-subtle)",
        border: "1px solid var(--divider)",
        fontSize: 10.5,
        fontWeight: 600,
        color: "var(--fg-subtle)",
        whiteSpace: "nowrap",
      }}
    >
      <StatusDot square size={7} color={scope.color} />
      {scope.label}
    </span>
  );
}

function BudgetLegend({ view }: { view: BudgetView }) {
  const b = view.budget;
  const fill = FILL_COLOR[view.geo.tone];
  const forecastOver =
    view.geo.forecastFrac !== null && view.geo.forecastFrac > view.geo.limitFrac;
  const legendItem: CSSProperties = { display: "inline-flex", alignItems: "center", gap: 5 };
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 14,
        flexWrap: "wrap",
        fontSize: 11,
        color: "var(--fg-subtle)",
      }}
    >
      <span style={legendItem}>
        <StatusDot square size={7} color={fill} />
        Actual{" "}
        <span className="mono" style={{ color: "var(--fg)" }}>
          {money(b.actualSpend, b.unit)}
        </span>
      </span>
      <span style={legendItem}>
        <span
          style={{
            width: 7,
            height: 7,
            borderRadius: 3,
            background: forecastOver ? "var(--tone-alarm-dot)" : fill,
            opacity: forecastOver ? 0.55 : 0.28,
            flex: "none",
          }}
        />
        Forecast{" "}
        <span className="mono" style={{ color: "var(--fg)" }}>
          {b.forecastedSpend !== null ? money(b.forecastedSpend, b.unit) : "--"}
        </span>
      </span>
      <span style={{ marginLeft: "auto" }}>
        Limit{" "}
        <span className="mono" style={{ color: "var(--fg)" }}>
          {money(b.limit, b.unit)}
        </span>
      </span>
    </div>
  );
}

function DetailRow({
  label,
  value,
  color,
  last,
}: {
  label: string;
  value: string;
  color?: string;
  last?: boolean;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "baseline",
        justifyContent: "space-between",
        gap: 12,
        padding: "7px 0",
        borderBottom: last ? "none" : "1px solid var(--divider)",
      }}
    >
      <span style={{ fontSize: 11, fontWeight: 600, color: "var(--fg-subtle)" }}>{label}</span>
      <span className="mono" style={{ fontSize: 13, fontWeight: 600, color: color ?? "var(--fg)" }}>
        {value}
      </span>
    </div>
  );
}

function BudgetDetail({ view }: { view: BudgetView }) {
  const b = view.budget;
  const over = view.geo.overAmount > 0;
  const headroom = b.limit - Math.max(b.actualSpend, b.forecastedSpend ?? 0);
  return (
    <div
      style={{
        borderTop: "1px solid var(--divider)",
        padding: "16px 20px 18px",
        display: "flex",
        flexDirection: "column",
        gap: 14,
      }}
    >
      <BudgetBar
        actual={b.actualSpend}
        forecast={b.forecastedSpend}
        limit={b.limit}
        currency={b.unit}
        height={18}
      />
      <div style={{ display: "flex", flexDirection: "column" }}>
        <DetailRow label="Actual" value={money(b.actualSpend, b.unit, 2)} />
        <DetailRow
          label="Forecast"
          value={b.forecastedSpend !== null ? money(b.forecastedSpend, b.unit, 2) : "--"}
        />
        <DetailRow label="Limit" value={money(b.limit, b.unit, 2)} />
        {over ? (
          <DetailRow
            label="Overage"
            value={`${money(view.geo.overAmount, b.unit, 2)} (+${view.geo.overPct.toFixed(0)}%)`}
            color="var(--tone-alarm-fg)"
            last
          />
        ) : (
          <DetailRow label="Headroom" value={money(headroom, b.unit, 2)} last />
        )}
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <Pill tone={view.actualPct === null ? "nodata" : view.pillTone}>{view.statusLabel}</Pill>
        <span style={{ fontSize: 11, color: "var(--fg-faint)" }}>
          gross usage · credits excluded · resets monthly
        </span>
      </div>
    </div>
  );
}

function BudgetCard({
  view,
  open,
  onToggle,
}: {
  view: BudgetView;
  open: boolean;
  onToggle: () => void;
}) {
  const b = view.budget;
  const cardStyle: CSSProperties = {};
  if (view.worstPct > 100) {
    cardStyle.borderColor = "color-mix(in srgb, var(--tone-alarm-dot) 40%, transparent)";
  }
  const pctLabel =
    view.forecastPct !== null
      ? `${Math.round(view.forecastPct)}% forecast`
      : view.actualPct !== null
        ? `${Math.round(view.actualPct)}% used`
        : "no limit";

  return (
    <Card flush style={cardStyle}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 16,
          width: "100%",
          padding: "18px 20px",
          background: "transparent",
          border: "none",
          font: "inherit",
          color: "inherit",
          textAlign: "left",
          cursor: "pointer",
        }}
      >
        <div className="budget-grid" style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span className="mono" style={{ fontSize: 13, fontWeight: 600, color: "var(--fg)" }}>
                {b.name}
              </span>
              <ScopePill scope={view.scope} />
            </div>
            <span
              className="mono"
              style={{ fontSize: 22, fontWeight: 600, color: "var(--fg)", lineHeight: 1.15 }}
            >
              {money(b.actualSpend, b.unit, 2)}
            </span>
            <span style={{ fontSize: 11, color: "var(--fg-faint)" }}>actual · month-to-date</span>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0 }}>
            <BudgetBar
              actual={b.actualSpend}
              forecast={b.forecastedSpend}
              limit={b.limit}
              currency={b.unit}
            />
            <BudgetLegend view={view} />
          </div>

          <div className="budget-grid__status">
            <Pill tone={view.actualPct === null ? "nodata" : view.pillTone}>
              <span className="mono">{pctLabel}</span>
            </Pill>
            <span className="mono" style={{ fontSize: 11.5, color: TONE_FG[view.pillTone] }}>
              {view.statusLabel}
            </span>
          </div>
        </div>
        <ChevronDown
          size={16}
          aria-hidden="true"
          style={{
            flex: "none",
            color: "var(--fg-faint)",
            transform: open ? "rotate(180deg)" : "none",
            transition: "transform var(--dur) var(--ease-out)",
          }}
        />
      </button>
      {open && <BudgetDetail view={view} />}
    </Card>
  );
}

function BudgetErrors({ errors }: { errors: SectionError[] }) {
  if (errors.length === 0) return null;
  return (
    <Card tight style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {errors.map((e, i) => (
        <span
          key={`${e.scope}-${i}`}
          style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "var(--tone-alarm-fg)" }}
        >
          <StatusDot tone="alarm" />
          <span>
            <strong>{e.scope}:</strong> {e.message}
          </span>
        </span>
      ))}
    </Card>
  );
}

function BudgetsBody({ data }: { data: BudgetsResponse }) {
  const [expandedName, setExpandedName] = useState<string | null>(null);
  const views = data.budgets
    .map(toView)
    .sort((a, b) => b.worstPct - a.worstPct || a.budget.name.localeCompare(b.budget.name));
  const flagged = views.filter((v) => v.flagged);
  const overCount = views.filter((v) => v.budget.status === "over").length;
  const currency = data.budgets[0]?.unit ?? "USD";
  const totalLimit = data.budgets.reduce((sum, b) => sum + b.limit, 0);
  const totalActual = data.budgets.reduce((sum, b) => sum + b.actualSpend, 0);
  const flaggedColor =
    overCount > 0 ? "var(--tone-alarm-fg)" : flagged.length > 0 ? "var(--tone-warn-fg)" : undefined;

  return (
    <>
      <div className="grid-kpi">
        <KpiCard
          label="Total budgets"
          icon={<Target size={15} aria-hidden="true" />}
          value={views.length}
          footnote="AWS Budgets · monthly cycle"
        />
        <KpiCard
          label="Flagged"
          icon={<Flag size={15} aria-hidden="true" />}
          value={flagged.length}
          valueColor={flaggedColor}
          badge={flagged.length > 0 ? (overCount > 0 ? "over limit" : "forecast") : undefined}
          badgeTone={overCount > 0 ? "alarm" : "warn"}
          footnote={flagged.length > 0 ? "actual or forecast over limit" : "all budgets on track"}
        />
        <KpiCard
          label="Total limit"
          icon={<Wallet size={15} aria-hidden="true" />}
          value={<span className="mono">{money(totalLimit, currency)}</span>}
          footnote="sum of monthly limits"
        />
        <KpiCard
          label="Total actual — gross"
          icon={<CircleDollarSign size={15} aria-hidden="true" />}
          value={<span className="mono">{money(totalActual, currency, 2)}</span>}
          footnote="month-to-date · credits excluded"
        />
      </div>

      {views.length === 0 ? (
        <Card>
          <EmptyState
            title="No budgets configured"
            message="No AWS Budgets found in the billing account."
            icon={<Target size={17} aria-hidden="true" />}
            tone="nodata"
          />
        </Card>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {views.map((v) => (
            <BudgetCard
              key={v.budget.name}
              view={v}
              open={expandedName === v.budget.name}
              onToggle={() =>
                setExpandedName((cur) => (cur === v.budget.name ? null : v.budget.name))
              }
            />
          ))}
        </div>
      )}

      <BudgetErrors errors={data.errors} />

      <p style={{ fontSize: 12, color: "var(--fg-faint)", maxWidth: 640 }}>
        Budgets track gross usage (credits excluded) · alerts via email to serigne@ + finance@
      </p>
    </>
  );
}

export function BudgetsPage({ refreshKey, onGeneratedAt }: BudgetsPageProps) {
  const { data, loading, error } = useApi<BudgetsResponse>(getBudgets, [], refreshKey);

  useEffect(() => {
    if (data) onGeneratedAt?.(data.generatedAt);
  }, [data, onGeneratedAt]);

  if (!data && loading) {
    return (
      <Card>
        <div className="state" role="status">
          <span className="spinner" aria-hidden="true" /> <span>Loading budgets...</span>
        </div>
      </Card>
    );
  }

  if (!data) {
    return (
      <Card>
        <EmptyState
          title="Failed to load budgets"
          message={error ?? "No budget data returned."}
          icon={<AlertTriangle size={17} aria-hidden="true" />}
          tone="alarm"
        />
      </Card>
    );
  }

  return <BudgetsBody data={data} />;
}
